export const SAME_POINT_PRECISION = 5; // ~1m at this precision -- close enough to call "the same point"

const CURVE_BEND = 0.2;
const CURVE_STEPS = 24;

function sameCoordinate(a: [number, number], b: [number, number]): boolean {
    return a[0].toFixed(SAME_POINT_PRECISION) === b[0].toFixed(SAME_POINT_PRECISION)
        && a[1].toFixed(SAME_POINT_PRECISION) === b[1].toFixed(SAME_POINT_PRECISION);
}

/** A quadratic Bezier arc from `a` to `b`, bent to its left (latitude-compensated
 * so North-South and East-West trips have the same visual curvature). */
function curveArc(a: [number, number], b: [number, number], steps = CURVE_STEPS): [number, number][] {
    const [lng1, lat1] = a;
    const [lng2, lat2] = b;
    const cosLat = Math.max(0.2, Math.cos((((lat1 + lat2) / 2) * Math.PI) / 180));
    const dLng = lng2 - lng1;
    const dLat = lat2 - lat1;
    const ctrlLng = (lng1 + lng2) / 2 - (dLat * CURVE_BEND) / cosLat;
    const ctrlLat = (lat1 + lat2) / 2 + dLng * cosLat * CURVE_BEND;
    return Array.from({ length: steps + 1 }, (_, index) => {
        const t = index / steps;
        return [
            (1 - t) ** 2 * lng1 + 2 * (1 - t) * t * ctrlLng + t ** 2 * lng2,
            (1 - t) ** 2 * lat1 + 2 * (1 - t) * t * ctrlLat + t ** 2 * lat2,
        ];
    });
}

/** Turns an ordered sequence of event coordinates into one curved LineString per
 * move between consecutive distinct places (preserving return trips A -> B -> A). */
export function curvedStepFeatures(
    orderedCoordinates: [number, number][],
    properties: GeoJSON.GeoJsonProperties = {},
): GeoJSON.Feature<GeoJSON.LineString>[] {
    const waypoints: [number, number][] = [];
    for (const coord of orderedCoordinates) {
        if (waypoints.length === 0 || !sameCoordinate(waypoints[waypoints.length - 1], coord)) {
            waypoints.push(coord);
        }
    }
    const arcs: GeoJSON.Feature<GeoJSON.LineString>[] = [];
    for (let i = 0; i + 1 < waypoints.length; i++) {
        arcs.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: curveArc(waypoints[i], waypoints[i + 1]) },
            properties,
        });
    }
    return arcs;
}

/** Bounding box covering all Point and LineString vertices (including curve arcs). */
export function boundsOfFeatures(features: GeoJSON.Feature[]): [number, number, number, number] | undefined {
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    let count = 0;
    const visit = (coord: number[]) => {
        const [lng, lat] = coord;
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
        count++;
    };
    for (const feature of features) {
        if (feature.geometry.type === 'Point') {
            visit(feature.geometry.coordinates);
        } else if (feature.geometry.type === 'LineString') {
            feature.geometry.coordinates.forEach(visit);
        }
    }
    return count === 0 ? undefined : [minLng, minLat, maxLng, maxLat];
}
